@if ((formState$ | ngrxPush).isSubmitting) {
  <div class="email-test-loading" role="status" aria-live="polite" aria-busy="true">
    <div class="email-test-loading-card">
      <app-shared-loading-g [animation]="animation"></app-shared-loading-g>
      <p>Testing email settings…</p>
    </div>
  </div>
}

<div class="email-settings" [class.email-settings-self-only]="!(formState$ | ngrxPush).canManageColleagueEmails">
    <!-- User Container — only when the user can admin other mailers for this store -->
    @if ((formState$ | ngrxPush).canManageColleagueEmails) {
      <div class="user-select-container">
        <!-- Search for User Container -->
        <div class="search-user-container">
          <app-shared-custom-search
            (searchChange)="emailSettingsManager.onQueryChange($event)"
            [debounce]="300"
            [placeholder]="'Search for user'"
            [searchAll]="(formState$ | ngrxPush).userQuery">
          </app-shared-custom-search>
        </div>
        <!-- User Select Container -->
        <div class="users-container">
          @for (colleague of (formState$ | ngrxPush).filteredColleagues; track colleague.id) {
            <button
              (click)="emailSettingsManager.onSelectColleague(colleague.id)"
              [ngClass]="{
              selected: colleague.id === (formState$ | ngrxPush).selectedColleague
            }"
              class="user-container"
              type="button">
              <div class="profile-picture-container">
                @if (colleague.pp) {
                  <img
                    [alt]="colleague.userName + 'Profile Picture'"
                    [src]="colleague.pp"
                    class="profile-picture"/>
                } @else {
                  <span class="initial-avatar">
                      {{ getUserNameInitials(colleague.userName) }}
                    </span>
                }
              </div>
              <div class="info-container">
                <div class="user-name">{{ colleague.userName }}</div>
                <small class="user-id">{{ colleague.id }}</small>
              </div>
            </button>
          }
        </div>
      </div>
    }
    <!-- Email Settings Container -->
    <div class="email-settings-container">
      <div class="form">
        <div class="header-container">
          <h3>
            Email Settings for
            <span>{{ (formState$ | ngrxPush).selectedColleague }}</span>
            at {{emailSettingsManager.currentState.selectedStore?.name}}
          </h3>
          <div class="header-clipboard-actions">
            @if (emailSettingsManager.copyFeedback() === 'copied') {
              <span class="clipboard-feedback">Settings copied!</span>
            }
            <app-shared-button
              [disabled]="!(formState$ | ngrxPush).selectedColleague"
              (buttonClicked)="emailSettingsManager.copySettings()"
              title="Copy these settings to clipboard">
              <app-display-icon
                [border]="false"
                [colour]="iconColourEnum.primary"
                [icon]="icons.contentCopy">
              </app-display-icon>
              Copy
            </app-shared-button>
            <app-shared-button
              [disabled]="!emailSettingsManager.clipboardHasContent() || !(formState$ | ngrxPush).selectedColleague"
              (buttonClicked)="emailSettingsManager.pasteSettings()"
              title="Paste copied settings into this user">
              <app-display-icon
                [border]="false"
                [colour]="iconColourEnum.primary"
                [icon]="icons.clipboard">
              </app-display-icon>
              Paste
            </app-shared-button>
          </div>
        </div>
        @if ((formState$ | ngrxPush).isLoadingSettings) {
          <div class="email-settings-loading" role="status" aria-live="polite" aria-busy="true">
            <app-shared-loading-g [animation]="animation"></app-shared-loading-g>
            <p>Loading email settings…</p>
          </div>
        } @else {
        <div class="content-container">
          <table>
            <tr>
              <td class="label">
                <p>Email account</p>
              </td>
              <td colspan="3">
                <div class="email-account-select">
                  <app-shared-custom-select
                    (selectionChange)="emailSettingsManager.onEmailAccountChange($event)"
                    [allowMultiple]="false"
                    [allowSelectAll]="false"
                    [autoSubmitOnSelect]="true"
                    [closeOnAutoSubmit]="true"
                    [isDisabled]="!!(formState$ | ngrxPush).microsoftMailBusyLabel"
                    [maxSelections]="1"
                    [placeholder]="'Select email account'"
                    [setOptions]="emailAccountOptions()"
                    [showActionButtons]="false"
                    [showChips]="false"
                    [showSearchBar]="false">
                  </app-shared-custom-select>
                </div>
              </td>
            </tr>
            @if (lastTestLabel()) {
              <tr>
                <td class="label">
                  <p>Last test</p>
                </td>
                <td colspan="3">
                  <p class="last-test" [class.last-test-failed]="lastTestFailed()">{{ lastTestLabel() }}</p>
                  @if (lastTestDetail()) {
                    <p class="last-test-detail">{{ lastTestDetail() }}</p>
                  }
                </td>
              </tr>
            }
            @if (emailSettingsManager.emailAccountChoice() === 'microsoft365') {
              <tr>
                <td class="label">
                  <p>Connected mailbox</p>
                </td>
                <td colspan="3">
                  <p class="connected-mailbox">
                    {{ mailboxAddress() || 'Not connected' }}
                  </p>
                </td>
              </tr>
              <tr>
                <td colspan="4">
                  <div class="microsoft-mail-actions">
                    <app-shared-button
                      [fill]="true"
                      [disabled]="microsoftActionsDisabled()"
                      (buttonClicked)="emailSettingsManager.connectMicrosoft365()">
                      Connect Microsoft 365
                    </app-shared-button>
                    @if (microsoftMailboxConnected()) {
                      <app-shared-button
                        [disabled]="microsoftActionsDisabled()"
                        (buttonClicked)="emailSettingsManager.disconnectMicrosoft365()">
                        Disconnect
                      </app-shared-button>
                    }
                    <app-shared-button
                      [disabled]="microsoftActionsDisabled()"
                      (buttonClicked)="emailSettingsManager.requestMicrosoftAdminConsent()">
                      Ask your Microsoft 365 admin to approve Gallix
                    </app-shared-button>
                  </div>
                  @if ((formState$ | ngrxPush).microsoftMailBusyLabel) {
                    <p class="microsoft-mail-status">{{ (formState$ | ngrxPush).microsoftMailBusyLabel }}</p>
                  }
                  @if (!emailSettingsManager.microsoftMailConfigured) {
                    <p class="microsoft-mail-note">
                      Microsoft 365 is not configured for this environment.
                    </p>
                  }
                </td>
              </tr>
            } @else {
            <!-- General Settings -->
            <tr>
              <td class="label">
                <p>Sender Address</p>
              </td>
              <td>
                <input
                  (input)="emailSettingsManager.onEmailChange($event)"
                  [class.error]="(formState$ | ngrxPush).formErrors?.email"
                  [class.required]="true"
                  [value]="(formState$ | ngrxPush).selectedColleagueEmailSettings?.email || ''"
                  aria-label="Sender Address"
                  name="sender-address"
                  placeholder="sender@example.com"
                  type="email"/>
              </td>
              <td class="label">
                <p>User Name</p>
              </td>
              <td>
                <input
                  (input)="emailSettingsManager.onUserChange($event)"
                  [class.error]="(formState$ | ngrxPush).formErrors?.user"
                  [class.required]="true"
                  [value]="(formState$ | ngrxPush).selectedColleagueEmailSettings?.user || ''"
                  aria-label="Email Address"
                  name="user-name"
                  placeholder="user@example.com"
                  type="email"/>
              </td>
            </tr>
            <tr>
              <td class="label">
                <p>Password</p>
              </td>
              <td>
                <input
                  (input)="emailSettingsManager.onPasswordChange($event)"
                  [class.error]="(formState$ | ngrxPush).formErrors?.pwd"
                  [class.required]="true"
                  [value]="(formState$ | ngrxPush).selectedColleagueEmailSettings?.pwd || ''"
                  aria-label="password"
                  class="masked-input"
                  name="email-settings-pwd"
                  type="text"/>
              </td>
              <td></td>
              <td></td>
            </tr>
            <!-- SMTP Settings -->
            <tr>
              <td colspan="4">
                <div class="section-header">
                  <h4>SMTP</h4>
                  <div class="dashed-line"></div>
                </div>
              </td>
            </tr>
            <tr>
              <td class="label">
                <p>Server Address</p>
              </td>
              <td>
                <input
                  (input)="emailSettingsManager.onSmtpServerChange($event)"
                  [class.error]="(formState$ | ngrxPush).formErrors?.smtp?.server"
                  [class.required]="true"
                  [value]="(formState$ | ngrxPush).selectedColleagueEmailSettings?.smtp?.server || ''"
                  aria-label="Server Address"
                  name="server-address"
                  placeholder="smtp.server.co.za"
                  type="email"/>
              </td>
              <td class="label">
                <p>Port Number</p>
              </td>
              <td>
                <input
                  (input)="emailSettingsManager.onSmtpPortChange($event)"
                  [class.error]="(formState$ | ngrxPush).formErrors?.smtp?.port"
                  [class.required]="true"
                  [value]="(formState$ | ngrxPush).selectedColleagueEmailSettings?.smtp?.port || ''"
                  aria-label="Port Number"
                  max="65535"
                  min="1"
                  name="port-number"
                  placeholder="587"
                  type="number"/>
              </td>
            </tr>
            <tr>
              <td class="label">
                <p>Use SSL/TLS</p>
              </td>
              <td class="switch-cell">
                <app-switch-ui
                  (checkedChange)="emailSettingsManager.onSmtpSslOnlyChange($event)"
                  [checked]="
                    (formState$ | ngrxPush).selectedColleagueEmailSettings?.smtp?.sslOnly || false
                  "
                  [size]="0.6">
                </app-switch-ui>
              </td>
            </tr>
            <!-- IMAP Settings -->
            <tr>
              <td colspan="4">
                <div class="section-header">
                  <h4>IMAP</h4>
                  <div class="dashed-line"></div>
                </div>
              </td>
            </tr>
            <tr>
              <td class="label">
                <p>Enable IMAP</p>
              </td>
              <td class="switch-cell">
                <app-switch-ui
                  (checkedChange)="emailSettingsManager.onEnableImapChange($event)"
                  [checked]="
                    (formState$ | ngrxPush).selectedColleagueEmailSettings?.enableImap || false
                  "
                  [size]="0.6">
                </app-switch-ui>
              </td>
            </tr>
            <tr>
              <td class="label">
                <p>Server Address</p>
              </td>
              <td>
                <input
                  (input)="emailSettingsManager.onImapServerChange($event)"
                  [class.error]="(formState$ | ngrxPush).formErrors?.imap?.server"
                  [class.required-if-enabled]="
                    (formState$ | ngrxPush).selectedColleagueEmailSettings?.enableImap
                  "
                  [disabled]="!(formState$ | ngrxPush).selectedColleagueEmailSettings?.enableImap"
                  [value]="(formState$ | ngrxPush).selectedColleagueEmailSettings?.imap?.server || ''"
                  aria-label="Server Address"
                  name="imap-server-address"
                  placeholder="imap.server.co.za"
                  type="text"/>
              </td>
            </tr>
            <tr>
              <td class="label">
                <p>Port Number</p>
              </td>
              <td>
                <input
                  (input)="emailSettingsManager.onImapPortChange($event)"
                  [class.error]="(formState$ | ngrxPush).formErrors?.imap?.port"
                  [class.required-if-enabled]="
                    (formState$ | ngrxPush).selectedColleagueEmailSettings?.enableImap
                  "
                  [disabled]="!(formState$ | ngrxPush).selectedColleagueEmailSettings?.enableImap"
                  [value]="(formState$ | ngrxPush).selectedColleagueEmailSettings?.imap?.port || ''"
                  aria-label="Port Number"
                  max="65535"
                  min="1"
                  name="imap-port-number"
                  placeholder="993"
                  type="number"/>
              </td>
            </tr>
            <tr>
              <td class="label">
                <p>Sent Mail Folder</p>
              </td>
              <td>
                <div class="info-text">
                  <p>
                    Emails will be organized into a new folder named
                    <b>"Gallix"</b>, with subfolders categorizing them by type.
                  </p>
                </div>
              </td>
            </tr>
            <tr>
              <td class="label">
                <p>Use SSL/TLS</p>
              </td>
              <td class="switch-cell">
                <app-switch-ui
                  (checkedChange)="emailSettingsManager.onImapSslOnlyChange($event)"
                  [checked]="
                    (formState$ | ngrxPush).selectedColleagueEmailSettings?.imap?.sslOnly || false
                  "
                  [disabled]="!(formState$ | ngrxPush).selectedColleagueEmailSettings?.enableImap"
                  [size]="0.6">
                </app-switch-ui>
              </td>
            </tr>
            }
          </table>
        </div>
        <div class="save-button-container">
          <app-shared-button
            [fill]="true"
            (buttonClicked)="emailSettingsManager.saveAndTestSettings()"
            [disabled]="isSaveButtonDisabled">
            Save & Test Email Settings
          </app-shared-button>
        </div>
        }
      </div>
    </div>
  </div>
